import type {
    AttributeInputKind,
    AttributeType,
    AttributeValueStatus,
} from '@/api/models';

/**
 * Presentational config + row shapes for the attributes page. Logic-only
 * (`.ts`); the views/modals import these and render. The page reads the full
 * types list (each type carries its `values[]`), so row shapes are the API
 * models verbatim.
 */

export type AttributeDictionaryTab = 'types' | 'pending';

/** Row in the Types table — the API `AttributeType` verbatim. */
export type AttributeTypeRow = AttributeType;

/** Stable column ids — shared between the View columns and sort descriptor. */
export const ATTR_TYPE_COLUMN = {
    name: 'name',
    slug: 'slug',
    inputKind: 'inputKind',
    isMultiSelect: 'isMultiSelect',
    valueCount: 'valueCount',
    actions: 'actions',
} as const;

/** Selectable input kinds, in display order, for the type form. */
export const ATTRIBUTE_INPUT_KINDS: AttributeInputKind[] = [
    'Select',
    'Text',
    'Number',
    'Boolean',
];

/** Only `Select` types own a value list (and can be multi-select). */
export const inputKindHasValues = (kind: AttributeInputKind): boolean =>
    kind === 'Select';

export type AttributeTypeRowAction = 'manageValues' | 'edit' | 'delete';

export const ATTR_TYPE_ROW_ACTIONS: {
    id: AttributeTypeRowAction;
    icon: string;
    labelKey: string;
    variant?: 'danger';
}[] = [
    {
        id: 'manageValues',
        icon: 'gravity-ui:list-ul',
        labelKey: 'attributes.components.dictionary.actions.manageValues',
    },
    {
        id: 'edit',
        icon: 'gravity-ui:pencil',
        labelKey: 'attributes.components.dictionary.actions.edit',
    },
    {
        id: 'delete',
        icon: 'gravity-ui:trash-bin',
        labelKey: 'attributes.components.dictionary.actions.delete',
        variant: 'danger',
    },
];

/** Chip color + label key per value lifecycle status. */
export const VALUE_STATUS_CHIP: Record<
    AttributeValueStatus,
    { color: 'success' | 'warning'; labelKey: string }
> = {
    Active: {
        color: 'success',
        labelKey: 'attributes.components.dictionary.status.active',
    },
    Pending: {
        color: 'warning',
        labelKey: 'attributes.components.dictionary.status.pending',
    },
};

/** Default rows shown per page in the types table (client-side pagination). */
export const TYPES_PAGE_SIZE = 10;

/** Selectable page sizes for the types table's rows-per-page control. */
export const TYPES_PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200];

/**
 * Compute the page numbers to render in a pagination control, collapsing long
 * ranges with `'ellipsis'` markers (first/last always shown, plus a window
 * around the current page). Pure — unit-tested directly.
 */
export const getPaginationWindow = (
    totalPages: number,
    current: number,
): (number | 'ellipsis')[] => {
    const pages: (number | 'ellipsis')[] = [];
    if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i += 1) {
            pages.push(i);
        }
        return pages;
    }
    pages.push(1);
    if (current > 3) {
        pages.push('ellipsis');
    }
    const start = Math.max(2, current - 1);
    const end = Math.min(totalPages - 1, current + 1);
    for (let i = start; i <= end; i += 1) {
        pages.push(i);
    }
    if (current < totalPages - 2) {
        pages.push('ellipsis');
    }
    pages.push(totalPages);
    return pages;
};

/**
 * Derive a **camelCase** slug from arbitrary text (used for the auto-slug).
 * Attribute slugs are camelCase keys (e.g. `ageGroup`, `apparelSize`) — the
 * stable contract the client keys translations off — so the first word stays
 * lowercase and each subsequent word is capitalized: `"Age Group"` → `ageGroup`.
 */
export const slugify = (value: string): string =>
    value
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter(Boolean)
        .map((word, index) =>
            index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1),
        )
        .join('');

/**
 * Sanitize a camelCase slug typed by hand: keep alphanumerics and drop
 * everything else, then lowercase the first character so the result is a valid
 * camelCase key while internal humps survive (`"AgeGroup"` → `ageGroup`).
 */
export const sanitizeSlugInput = (value: string): string => {
    const cleaned = value.replace(/[^a-zA-Z0-9]/g, '');
    return cleaned.charAt(0).toLowerCase() + cleaned.slice(1);
};
